import { useState, useCallback } from 'react';

/**
 * Custom hook for JetSetter travel preferences management
 * @returns {Object} Travel preferences state and methods
 */
const useTravelPreferences = () => {
  const [preferences, setPreferences] = useState({
    // Flight Preferences
    preferredClass: 'economy',
    seatPreference: 'window',
    mealPreference: 'standard',
    
    // Booking Preferences
    preferredAirlines: ['JetScanner Airways', 'Sky Connect'],
    maxLayovers: 1,
    preferredDepartureTime: 'morning',
    
    // Personal Preferences
    frequentFlyerPrograms: [
      { airline: 'JetScanner Airways', number: 'JS123456789' }
    ],
    
    // Accessibility
    wheelchairAssistance: false,
    specialAssistance: '',
    
    // Communication
    language: 'en',
    currency: 'USD',
    timezone: 'America/New_York',
    
    // Notifications
    flightReminders: true,
    priceAlerts: true,
    promotionalEmails: false,
    
    // Travel Documents
    passportNumber: '',
    passportExpiry: '',
    visaRequired: false,
    
    // Emergency Contact
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTravelPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mock preferences are already set in initial state
      // In real app, this would fetch from API and setPreferences(data)
      
    } catch (err) {
      console.error('Failed to load travel preferences:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTravelPreferences = useCallback(async (newPreferences) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedPreferences = { ...preferences, ...newPreferences };
      setPreferences(updatedPreferences);
      
      return updatedPreferences;
    } catch (err) {
      console.error('Failed to update travel preferences:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [preferences]);

  const resetToDefaults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const defaultPreferences = {
        preferredClass: 'economy',
        seatPreference: 'window',
        mealPreference: 'standard',
        preferredAirlines: [],
        maxLayovers: 2,
        preferredDepartureTime: 'any',
        frequentFlyerPrograms: [],
        wheelchairAssistance: false,
        specialAssistance: '',
        language: 'en',
        currency: 'USD',
        timezone: 'America/New_York',
        flightReminders: true,
        priceAlerts: false,
        promotionalEmails: false,
        passportNumber: '',
        passportExpiry: '',
        visaRequired: false,
        emergencyContact: {
          name: '',
          phone: '',
          relationship: ''
        }
      };
      
      setPreferences(defaultPreferences);
      return defaultPreferences;
    } catch (err) {
      console.error('Failed to reset preferences:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const addFrequentFlyerProgram = useCallback(async (program) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedPrograms = [...preferences.frequentFlyerPrograms, program];
      const updatedPreferences = { 
        ...preferences, 
        frequentFlyerPrograms: updatedPrograms 
      };
      
      setPreferences(updatedPreferences);
      return updatedPreferences;
    } catch (err) {
      console.error('Failed to add frequent flyer program:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [preferences]);

  const removeFrequentFlyerProgram = useCallback(async (programIndex) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const updatedPrograms = preferences.frequentFlyerPrograms.filter((_, index) => index !== programIndex);
      const updatedPreferences = { 
        ...preferences, 
        frequentFlyerPrograms: updatedPrograms 
      };
      
      setPreferences(updatedPreferences);
      return updatedPreferences;
    } catch (err) {
      console.error('Failed to remove frequent flyer program:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [preferences]);

  const updateEmergencyContact = useCallback(async (contactInfo) => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const updatedPreferences = { 
        ...preferences, 
        emergencyContact: { ...preferences.emergencyContact, ...contactInfo }
      };
      
      setPreferences(updatedPreferences);
      return updatedPreferences;
    } catch (err) {
      console.error('Failed to update emergency contact:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [preferences]);

  return {
    preferences,
    loading,
    error,
    loadTravelPreferences,
    updateTravelPreferences,
    resetToDefaults,
    addFrequentFlyerProgram,
    removeFrequentFlyerProgram,
    updateEmergencyContact
  };
};

export default useTravelPreferences;